/**
 * FX: load from /config/fx_rates.json.
 * Supports legacy flat { USD, GBP, … } or monthly { byMonth: { "2025-04": { USD: … } } }.
 * Lookup walks backward month-by-month until a rate exists, then flat/seed fallback.
 */

const LS_KEY = 'fx_rates_cache_v2';

const FALLBACK_RATES: Record<string, number> = {
  INR: 1,
  USD: 84,
  GBP: 107,
  EUR: 91,
};

interface FxCachePayload {
  flat: Record<string, number>;
  byMonth: Record<string, Record<string, number>>;
}

function normalizePayload(data: unknown): FxCachePayload {
  if (!data || typeof data !== 'object') {
    return { flat: { ...FALLBACK_RATES }, byMonth: {} };
  }
  const o = data as Record<string, unknown>;
  const flat: Record<string, number> = { ...FALLBACK_RATES };
  const byMonthRaw = o.byMonth;

  if (byMonthRaw && typeof byMonthRaw === 'object' && !Array.isArray(byMonthRaw)) {
    for (const [k, v] of Object.entries(o)) {
      if (k === 'byMonth' || k === 'meta') continue;
      if (typeof v === 'number') flat[String(k).toUpperCase()] = v;
    }
    const bm: Record<string, Record<string, number>> = {};
    for (const [ym, row] of Object.entries(byMonthRaw as Record<string, unknown>)) {
      if (!row || typeof row !== 'object' || Array.isArray(row)) continue;
      const inner: Record<string, number> = {};
      for (const [ccy, n] of Object.entries(row as Record<string, unknown>)) {
        if (typeof n === 'number' && Number.isFinite(n)) {
          inner[String(ccy).toUpperCase()] = n;
        }
      }
      bm[ym] = inner;
    }
    return { flat, byMonth: bm };
  }

  for (const [k, v] of Object.entries(o)) {
    if (k === 'meta') continue;
    if (typeof v === 'number') flat[String(k).toUpperCase()] = v;
  }
  return { flat, byMonth: {} };
}

function loadFromStorage(): FxCachePayload | null {
  try {
    const raw = localStorage.getItem(LS_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return normalizePayload(parsed);
  } catch {
    return null;
  }
}

function saveToStorage(payload: FxCachePayload): void {
  try {
    localStorage.setItem(
      LS_KEY,
      JSON.stringify({ ...payload.flat, byMonth: payload.byMonth }),
    );
  } catch {
    /* ignore */
  }
}

let _payload: FxCachePayload = loadFromStorage() ?? {
  flat: { ...FALLBACK_RATES },
  byMonth: {},
};
let _loaded = false;

function prevMonthYm(ym: string): string {
  const [y, m] = ym.split('-').map(Number);
  const d = new Date(y, m - 2, 1);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
}

function latestYmInTable(byMonth: Record<string, Record<string, number>>): string {
  const keys = Object.keys(byMonth).sort();
  return keys.length ? keys[keys.length - 1] : '';
}

function getRateForCurrency(currency: string, invoiceDate?: string): number {
  const ccy = (currency || 'INR').toUpperCase();
  if (ccy === 'INR') return 1;

  const { flat, byMonth } = _payload;
  const months = byMonth;

  if (!Object.keys(months).length) {
    return flat[ccy] ?? FALLBACK_RATES[ccy] ?? 1;
  }

  let ym =
    invoiceDate && invoiceDate.length >= 7
      ? invoiceDate.slice(0, 7)
      : latestYmInTable(months);

  if (!ym || !/^\d{4}-\d{2}$/.test(ym)) {
    ym = latestYmInTable(months);
  }

  let cur = ym;
  for (let i = 0; i < 600; i += 1) {
    const row = months[cur];
    const r = row?.[ccy];
    if (r != null && Number.isFinite(r)) return r;
    const prev = prevMonthYm(cur);
    if (prev === cur) break;
    cur = prev;
  }

  return flat[ccy] ?? FALLBACK_RATES[ccy] ?? 1;
}

/** Fetch rates from public/config/fx_rates.json — call once at app init or on demand. */
export async function loadFxRates(): Promise<void> {
  if (_loaded) return;
  try {
    const res = await fetch('/config/fx_rates.json');
    if (res.ok) {
      const data = await res.json();
      _payload = normalizePayload(data);
      saveToStorage(_payload);
    }
  } catch {
    /* keep cache / defaults */
  }
  _loaded = true;
}

export interface ToInrContext {
  /** YYYY-MM-DD (or ISO); selects that month’s row in byMonth, then walks back if missing. */
  invoiceDate?: string;
}

/** Convert an amount in any supported currency to INR. */
export function toInr(
  amount: number,
  currency: string,
  context?: ToInrContext,
): number {
  const rate = getRateForCurrency(currency, context?.invoiceDate);
  return amount * rate;
}

/**
 * Snapshot for UI footers: latest month row merged with top-level flat file keys.
 */
export function getFxRates(): Record<string, number> {
  const { flat, byMonth } = _payload;
  const latest = latestYmInTable(byMonth);
  if (!latest) return { ...FALLBACK_RATES, ...flat };
  return { INR: 1, ...flat, ...byMonth[latest] };
}
